/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type {
  DashboardResponse, OverviewMode, OverviewTiles, OverviewView,
} from '@/services/housien/dashboard';
import { stageLabel } from './Overview.stages';
import { stageBoardUrl } from './Overview.stage-filter';
import type { Tile } from './Overview.tile';

type TFn = (key: string, options?: Record<string, unknown>) => string;

/** Everything a card needs to compute itself. */
export interface TileContext {
  t: TFn;
  data: DashboardResponse | null;
  agencyId: string;
  /** The loss-reason vocabulary lives in another namespace. */
  lossLabel: (reason: string) => string;
}

/**
 * The picker's sections. They only organise the list in the dialog: the
 * screen is one grid, in the order the person chose.
 */
export type TileCategory = 'action' | 'portfolio' | 'intake' | 'performance' | 'funnel';

export const TILE_CATEGORIES: TileCategory[] = [
  'action', 'portfolio', 'intake', 'performance', 'funnel',
];

/** One card the overview can show. */
export interface CatalogueEntry {
  id: string;
  category: TileCategory;
  build: (ctx: TileContext) => Tile;
}

/** How many cards each mode holds. The server refuses more. */
export const TILE_LIMITS: Record<OverviewMode, number> = { basic: 6, advanced: 14 };

const ACCENT = {
  marinho: 'var(--accent-marinho)',
  ameixa: 'var(--accent-ameixa)',
  cobre: 'var(--accent-cobre)',
  mostarda: 'var(--accent-mostarda)',
  petroleo: 'var(--accent-petroleo)',
  salvia: 'var(--accent-salvia)',
  terracota: 'var(--accent-terracota)',
};

/**
 * An em dash reads as "there is nothing to measure"; a zero reads as an
 * answer of zero. The payload distinguishes the two deliberately — an empty
 * portfolio has no vacancy rate, and nothing having closed yet is not a
 * stock age of zero days — so the screen has to as well.
 */
function orDash(value: number | null | undefined, format: (n: number) => string): string {
  return value === null || value === undefined ? '—' : format(value);
}

/**
 * Money.
 */
function money(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency', currency: 'BRL',
    maximumFractionDigits: 0, minimumFractionDigits: 0,
  });
}

/** Millions abbreviated: a VGV written in full does not fit a 210px tile. */
function compactMoney(value: number): string {
  if (Math.abs(value) < 1_000_000) return money(value);
  return value.toLocaleString('pt-BR', {
    style: 'currency', currency: 'BRL',
    notation: 'compact', maximumFractionDigits: 1,
  });
}

/** The `f` param both the properties and the visits lists read. */
function encodeFilters(filters: Record<string, unknown>): string {
  return btoa(JSON.stringify(filters))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** The properties list, carrying the same base64url `f` the panel writes. */
function propertiesUrl(agencyId: string, filters: Record<string, unknown>): string {
  return `/app/agencies/${agencyId}/properties?f=${encodeFilters(filters)}`;
}

/** The day `count` days ago, as the ISO date the filters speak. */
function daysAgo(count: number): string {
  const day = new Date();
  day.setDate(day.getDate() - count);
  return day.toISOString().slice(0, 10);
}

/** The visits list, narrowed to a period it already knows how to read. */
function visitsUrl(agencyId: string, period: 'today' | 'thisWeek'): string {
  return `/app/agencies/${agencyId}/visits?f=${encodeFilters({ period })}`;
}

/** Open attendances, summed over every stage the board holds. */
function openAttendances(data: DashboardResponse | null): number {
  return Object.values(data?.attendance_by_stage ?? {}).reduce((sum, count) => sum + count, 0);
}

/**
 * The three biggest categories, named. The book has a long tail and the
 * context line is one line: the tail is what the properties list is for.
 */
function topCategories(counts: Record<string, number> | undefined): string {
  return Object.entries(counts ?? {})
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([category, count]) => `${count} ${category.toLowerCase()}`)
    .join(' · ');
}

/**
 * The per-stage breakdown, capped at the three busiest stages.
 *
 * All eight stages named in full ran to four lines in a 185px tile and
 * pushed the group taller than the fold allows. The rest are counted
 * rather than dropped -- a breakdown that silently omits stages would
 * stop adding up to the number above it.
 */
function stageBreakdown(t: TFn, byStage: Record<string, number> | undefined): string {
  const entries = Object.entries(byStage ?? {})
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);
  const named = entries.slice(0, 3)
    .map(([stage, count]) => `${count} ${stageLabel(t, stage).toLowerCase()}`);
  const rest = entries.slice(3).reduce((sum, [, count]) => sum + count, 0);
  return [
    ...named,
    rest ? t('dashboard.tiles.attendances.otherStages', { count: rest }) : '',
  ].filter(Boolean).join(' · ');
}

/**
 * The vacancy tile's context line: how long a vacant listing sits on average,
 * or the line that admits we have nothing to average yet.
 */
function vacancyHint(t: TFn, averageDays: number | null | undefined): string {
  if (averageDays === null || averageDays === undefined) return t('dashboard.tiles.vacancy.noAverage');
  return t('dashboard.tiles.vacancy.average', { count: averageDays });
}

/**
 * The stock-days unit, suppressed while nothing has closed -- a dash carries
 * no unit.
 */
function stockDaysUnit(t: TFn, averageStockDays: number | null | undefined): string | undefined {
  return averageStockDays == null ? undefined : t('dashboard.tiles.stockDays.unit');
}

/**
 * The stock-days context line, in its empty and its measured form.
 */
function stockDaysHint(t: TFn, averageStockDays: number | null | undefined): string {
  return averageStockDays == null
    ? t('dashboard.tiles.stockDays.nothingClosed')
    : t('dashboard.tiles.stockDays.hint');
}

/**
 * The sales-volume context line, which names the listings priced at nothing.
 */
function salesVolumeHint(t: TFn, withoutPrice: number | null | undefined): string {
  return withoutPrice
    ? t('dashboard.tiles.salesVolume.withoutPrice', { count: withoutPrice })
    : t('dashboard.tiles.salesVolume.hint');
}

/** The visits-today context line: the next one, or that there is none. */
function visitsTodayHint(t: TFn, data: DashboardResponse | null): string {
  const visits = data?.visits_today ?? [];
  return visits.length
    ? t('dashboard.tiles.visitsToday.next', { time: visits[0]?.time ?? '' })
    : t('dashboard.tiles.visitsToday.none');
}

/** The overdue-rent context line: what it counts, how much, how old. */
function overdueRentHint(t: TFn, data: DashboardResponse | null): string {
  const overdue = data?.overdue_rent;
  const oldest = overdue?.items?.[0]?.days_late;
  return [
    t('dashboard.tiles.overdueRent.what'),
    money((overdue?.total_cents ?? 0) / 100),
    oldest ? t('dashboard.tiles.overdueRent.oldest', { count: oldest }) : '',
  ].filter(Boolean).join(' · ');
}

/**
 * The expiring-contracts context line.
 *
 * `without_end_date` declares what the count could not cover. It is not
 * dropped for a tidier line: a contract with no term recorded is invisible
 * to this number, and saying so is the difference between a figure and a
 * claim.
 */
function expiringHint(t: TFn, data: DashboardResponse | null): string {
  const expiring = data?.expiring_contracts;
  return [
    t('dashboard.tiles.expiringContracts.window', { count: expiring?.window_days ?? 0 }),
    expiring?.without_end_date
      ? t('dashboard.tiles.expiringContracts.withoutEndDate', { count: expiring.without_end_date })
      : '',
  ].filter(Boolean).join(' · ');
}

/**
 * The two most frequent loss reasons, named. The payload keeps the top
 * five; the card has one line.
 */
function lossHint(ctx: TileContext): string {
  const reasons = ctx.data?.kpis?.loss_reasons ?? [];
  if (!reasons.length) return ctx.t('dashboard.tiles.lossReasons.none');
  return reasons.slice(0, 2)
    .map((item) => `${item.count} ${ctx.lossLabel(item.reason).toLowerCase()}`)
    .join(' · ');
}

type FunnelKey = 'active_leads' | 'in_negotiation' | 'proposal_sent' | 'closed_this_month';

/**
 * One funnel bucket as a card: its count, who arrived last, and the board
 * filtered to the stages it is made of.
 */
function funnelTile(ctx: TileContext, key: FunnelKey, id: string, i18n: string): Tile {
  const stage = ctx.data?.sales_funnel?.[key];
  const last = stage?.count ? stage.last_client : '';
  return {
    id,
    value: String(stage?.count ?? 0),
    label: ctx.t(`dashboard.tiles.${i18n}.label`),
    hint: [
      ctx.t(`dashboard.tiles.${i18n}.hint`),
      last ? ctx.t('dashboard.tiles.funnel.last', { name: last }) : '',
    ].filter(Boolean).join(' · '),
    accent: ACCENT.terracota,
    to: stageBoardUrl(ctx.agencyId, key),
  };
}

/* ------------------------------------------------------------------ */
/* Cards that read the same in both views                              */
/* ------------------------------------------------------------------ */

const visitsToday: CatalogueEntry = {
  id: 'visits-today',
  category: 'action',
  build: ({ t, data, agencyId }) => ({
    id: 'visits-today',
    value: String(data?.visits_today?.length ?? 0),
    label: t('dashboard.tiles.visitsToday.label'),
    hint: visitsTodayHint(t, data),
    accent: ACCENT.petroleo,
    to: visitsUrl(agencyId, 'today'),
  }),
};

const staleProperties: CatalogueEntry = {
  id: 'stale-properties',
  category: 'action',
  build: ({ t, data, agencyId }) => {
    const windowDays = data?.properties_without_recent_visits?.window_days ?? 30;
    return {
      id: 'stale-properties',
      value: String(data?.properties_without_recent_visits?.count ?? 0),
      label: t('dashboard.tiles.staleProperties.label'),
      hint: t('dashboard.tiles.staleProperties.hint', { count: windowDays }),
      accent: ACCENT.cobre,
      to: propertiesUrl(agencyId, { days_since_visit_gte: windowDays }),
    };
  },
};

const vacancy: CatalogueEntry = {
  id: 'vacancy',
  category: 'portfolio',
  build: ({ t, data, agencyId }) => ({
    id: 'vacancy',
    value: String(data?.vacancy?.vacant ?? 0),
    unit: t('dashboard.tiles.vacancy.unit'),
    label: t('dashboard.tiles.vacancy.label'),
    hint: vacancyHint(t, data?.vacancy?.average_days),
    accent: ACCENT.petroleo,
    to: propertiesUrl(agencyId, { status: 'active', transaction_type: ['Aluguel'] }), // i18n-exempt: backend transaction_type vocabulary, sent as a query value
  }),
};

const salesVolume: CatalogueEntry = {
  id: 'sales-volume',
  category: 'portfolio',
  build: ({ t, data, agencyId }) => ({
    id: 'sales-volume',
    value: compactMoney(data?.sales_volume?.available ?? 0),
    label: t('dashboard.tiles.salesVolume.label'),
    hint: salesVolumeHint(t, data?.sales_volume?.without_price),
    accent: ACCENT.salvia,
    to: propertiesUrl(agencyId, { transaction_type: ['Venda'] }), // i18n-exempt: backend transaction_type vocabulary, sent as a query value
  }),
};

const newListings: CatalogueEntry = {
  id: 'new-listings',
  category: 'intake',
  build: ({ t, data, agencyId }) => {
    const windowDays = data?.new_listings?.window_days ?? 30;
    return {
      id: 'new-listings',
      value: String(data?.new_listings?.count ?? 0),
      label: t('dashboard.tiles.newListings.label'),
      hint: t('dashboard.tiles.newListings.hint', { count: windowDays }),
      accent: ACCENT.salvia,
      to: propertiesUrl(agencyId, { created_after: daysAgo(windowDays) }),
    };
  },
};

const incompleteListings: CatalogueEntry = {
  id: 'incomplete-listings',
  category: 'intake',
  build: ({ t, data, agencyId }) => ({
    id: 'incomplete-listings',
    value: String(data?.incomplete_listings?.count ?? 0),
    label: t('dashboard.tiles.incompleteListings.label'),
    hint: t('dashboard.tiles.incompleteListings.hint'),
    accent: ACCENT.mostarda,
    to: propertiesUrl(agencyId, { publication_blocked: true }),
  }),
};

const visitsWeek: CatalogueEntry = {
  id: 'visits-week',
  category: 'performance',
  build: ({ t, data, agencyId }) => ({
    id: 'visits-week',
    value: String(data?.kpis?.visits_this_week?.value ?? 0),
    label: t('dashboard.tiles.visitsWeek.label'),
    hint: t('dashboard.tiles.visitsWeek.hint'),
    accent: ACCENT.petroleo,
    to: visitsUrl(agencyId, 'thisWeek'),
  }),
};

/*
 * A rate is not a set, so this one cannot open "the list behind the
 * number" the way the others do. It opens the set it is computed from --
 * every attendance -- which is the nearest honest thing.
 */
const conversionRate: CatalogueEntry = {
  id: 'conversion-rate',
  category: 'performance',
  build: ({ t, data, agencyId }) => ({
    id: 'conversion-rate',
    value: orDash(data?.kpis?.conversion_rate?.value, (n) => `${Math.round(n)}%`),
    label: t('dashboard.tiles.conversionRate.label'),
    hint: t('dashboard.tiles.conversionRate.hint'),
    accent: ACCENT.ameixa,
    to: `/app/agencies/${agencyId}/attendances`,
  }),
};

const lossReasons: CatalogueEntry = {
  id: 'loss-reasons',
  category: 'performance',
  build: (ctx) => ({
    id: 'loss-reasons',
    value: String((ctx.data?.kpis?.loss_reasons ?? []).reduce((sum, item) => sum + item.count, 0)),
    label: ctx.t('dashboard.tiles.lossReasons.label'),
    hint: lossHint(ctx),
    accent: ACCENT.ameixa,
    to: `/app/agencies/${ctx.agencyId}/attendances?stages=lost`,
  }),
};

const FUNNEL: CatalogueEntry[] = [
  { id: 'funnel-active-leads', category: 'funnel', build: (ctx) => funnelTile(ctx, 'active_leads', 'funnel-active-leads', 'funnelActiveLeads') },
  { id: 'funnel-in-negotiation', category: 'funnel', build: (ctx) => funnelTile(ctx, 'in_negotiation', 'funnel-in-negotiation', 'funnelVisiting') },
  { id: 'funnel-proposal-sent', category: 'funnel', build: (ctx) => funnelTile(ctx, 'proposal_sent', 'funnel-proposal-sent', 'funnelNegotiating') },
  { id: 'funnel-closed-month', category: 'funnel', build: (ctx) => funnelTile(ctx, 'closed_this_month', 'funnel-closed-month', 'funnelClosedMonth') },
];

/* ------------------------------------------------------------------ */
/* Agency administration: rent, contracts and the book as a whole      */
/* ------------------------------------------------------------------ */

const overdueRent: CatalogueEntry = {
  id: 'overdue-rent',
  category: 'action',
  build: ({ t, data, agencyId }) => ({
    id: 'overdue-rent',
    value: String(data?.overdue_rent?.count ?? 0),
    label: t('dashboard.tiles.overdueRent.label'),
    hint: overdueRentHint(t, data),
    accent: ACCENT.cobre,
    to: `/app/agencies/${agencyId}/leases?overdue=true`,
  }),
};

const expiringContracts: CatalogueEntry = {
  id: 'expiring-contracts',
  category: 'action',
  build: ({ t, data, agencyId }) => ({
    id: 'expiring-contracts',
    value: String(data?.expiring_contracts?.count ?? 0),
    label: t('dashboard.tiles.expiringContracts.label'),
    hint: expiringHint(t, data),
    accent: ACCENT.mostarda,
    to: `/app/agencies/${agencyId}/leases?ending_within=${data?.expiring_contracts?.window_days ?? 60}`,
  }),
};

/*
 * The number averages the listings that closed; the list orders the ones
 * still standing. Different sets on purpose: the average is the answer,
 * and this is the question it provokes -- which ones are dragging it.
 */
const stockDays: CatalogueEntry = {
  id: 'stock-days',
  category: 'portfolio',
  build: ({ t, data, agencyId }) => ({
    id: 'stock-days',
    value: orDash(data?.average_stock_days, (n) => String(Math.round(n))),
    unit: stockDaysUnit(t, data?.average_stock_days),
    label: t('dashboard.tiles.stockDays.label'),
    hint: stockDaysHint(t, data?.average_stock_days),
    accent: ACCENT.petroleo,
    to: propertiesUrl(agencyId, { sort: 'stock_days' }),
  }),
};

/*
 * The payload's own count, not the sum of `category_counts`. That histogram
 * skips any listing whose category is null or outside the backend
 * vocabulary, and summing it under a label that says "na carteira"
 * under-reported a thousand-listing book by a hundred and twenty-two.
 */
const portfolioTotal: CatalogueEntry = {
  id: 'portfolio-total',
  category: 'portfolio',
  build: ({ t, data, agencyId }) => ({
    id: 'portfolio-total',
    value: orDash(data?.total_properties, String),
    label: t('dashboard.tiles.portfolioTotal.label'),
    hint: topCategories(data?.category_counts) || t('dashboard.tiles.portfolioTotal.empty'),
    accent: ACCENT.marinho,
    to: `/app/agencies/${agencyId}/properties`,
  }),
};

/*
 * No list behind it yet: the properties list cannot be filtered to "sold
 * this month", and opening every listing for sale would answer a different
 * question. Plain text until that filter exists -- see `OverviewTile`.
 */
const salesRealizedMonth: CatalogueEntry = {
  id: 'sales-realized-month',
  category: 'portfolio',
  build: ({ t, data }) => ({
    id: 'sales-realized-month',
    value: compactMoney(data?.sales_volume?.realized_month ?? 0),
    label: t('dashboard.tiles.salesRealizedMonth.label'),
    hint: t('dashboard.tiles.salesRealizedMonth.hint', { count: data?.sales_volume?.realized_count ?? 0 }),
    accent: ACCENT.salvia,
  }),
};

const attendances: CatalogueEntry = {
  id: 'attendances',
  category: 'intake',
  build: ({ t, data, agencyId }) => ({
    id: 'attendances',
    value: String(openAttendances(data)),
    unit: t('dashboard.tiles.attendances.unit'),
    label: t('dashboard.tiles.attendances.label'),
    hint: stageBreakdown(t, data?.attendance_by_stage) || t('dashboard.tiles.attendances.empty'),
    accent: ACCENT.terracota,
    to: `/app/agencies/${agencyId}/attendances`,
  }),
};

/*
 * The payload's key is `leads_without_return`; the screen says clients,
 * because that is the word anyone in the office uses.
 */
const contactsWithoutReturn: CatalogueEntry = {
  id: 'contacts-without-return',
  category: 'performance',
  build: ({ t, data, agencyId }) => ({
    id: 'contacts-without-return',
    value: String(data?.kpis?.leads_without_return?.value ?? 0),
    label: t('dashboard.tiles.contactsWithoutReturn.label'),
    hint: t('dashboard.tiles.contactsWithoutReturn.hint'),
    accent: ACCENT.cobre,
    to: `/app/agencies/${agencyId}/clients?f=${encodeFilters({ withoutReturn: true })}`,
  }),
};

/* ------------------------------------------------------------------ */
/* A broker's own view                                                 */
/* ------------------------------------------------------------------ */
/*
 * Every card here reads a payload the server already narrowed to the
 * caller (`?scope=me`), and every destination is a list the server narrows
 * the same way on its own -- which is why no link carries a broker
 * parameter. Rent, contracts, stock age and realized sales are not offered:
 * the scoped payload empties the axes they read, and a card reading zero
 * would be read as "nothing overdue".
 */

const myAttendances: CatalogueEntry = {
  id: 'my-attendances',
  category: 'intake',
  build: (ctx) => ({
    ...attendances.build(ctx),
    id: 'my-attendances',
    label: ctx.t('dashboard.tiles.myAttendances.label'),
  }),
};

/*
 * The count is the panel's own list, not a KPI: the scoped payload keeps
 * no per-member history, so `leads_without_return` comes back without a
 * delta and this is the figure behind it anyway.
 */
const myContactsWithoutReturn: CatalogueEntry = {
  id: 'my-contacts-without-return',
  category: 'performance',
  build: ({ t, data, agencyId }) => ({
    id: 'my-contacts-without-return',
    value: String(data?.clients_without_return?.length ?? 0),
    label: t('dashboard.tiles.contactsWithoutReturn.label'),
    hint: t('dashboard.tiles.myContactsWithoutReturn.hint'),
    accent: ACCENT.cobre,
    to: `/app/agencies/${agencyId}/clients?f=${encodeFilters({ withoutReturn: true })}`,
  }),
};

/*
 * "Mine" here is the product's own rule -- assigned to me, or assigned to
 * nobody -- which is exactly what the properties list shows this broker.
 */
const myProperties: CatalogueEntry = {
  id: 'my-properties',
  category: 'portfolio',
  build: ({ t, data, agencyId }) => ({
    id: 'my-properties',
    value: orDash(data?.total_properties, String),
    label: t('dashboard.tiles.myProperties.label'),
    hint: topCategories(data?.category_counts) || t('dashboard.tiles.myProperties.empty'),
    accent: ACCENT.marinho,
    to: `/app/agencies/${agencyId}/properties`,
  }),
};

/**
 * Every card each view can show, in the picker's order. Mirrors the
 * server's `AGENCY_TILE_IDS` / `ME_TILE_IDS`: a card added here and not
 * there is refused on save.
 */
export const TILE_CATALOGUE: Record<OverviewView, CatalogueEntry[]> = {
  agency: [
    visitsToday, overdueRent, expiringContracts, staleProperties,
    vacancy, stockDays, portfolioTotal, salesVolume, salesRealizedMonth,
    newListings, incompleteListings, attendances,
    visitsWeek, contactsWithoutReturn, conversionRate, lossReasons,
    ...FUNNEL,
  ],
  me: [
    visitsToday, staleProperties,
    vacancy, myProperties, salesVolume,
    newListings, incompleteListings, myAttendances,
    visitsWeek, myContactsWithoutReturn, conversionRate, lossReasons,
    ...FUNNEL,
  ],
};

/**
 * What somebody sees before choosing: the screens as they were when the
 * cards were fixed, so nothing moves on the day this ships.
 */
export const DEFAULT_TILES: OverviewTiles = {
  agency: {
    basic: [
      'visits-today', 'overdue-rent', 'expiring-contracts', 'stale-properties',
      'portfolio-total', 'sales-volume',
    ],
    advanced: [
      'visits-today', 'overdue-rent', 'expiring-contracts', 'stale-properties',
      'vacancy', 'stock-days', 'portfolio-total', 'sales-volume',
      'new-listings', 'incomplete-listings', 'attendances',
      'visits-week', 'contacts-without-return', 'conversion-rate',
    ],
  },
  me: {
    basic: ['visits-today', 'my-attendances', 'my-contacts-without-return', 'my-properties'],
    advanced: [
      'visits-today', 'my-attendances', 'my-contacts-without-return', 'my-properties',
      'visits-week', 'conversion-rate', 'stale-properties', 'new-listings',
      'incomplete-listings', 'sales-volume', 'funnel-proposal-sent', 'funnel-closed-month',
    ],
  },
};

/** A tiles document with no choice made, for before the stored one lands. */
export const NO_CHOICE: OverviewTiles = {
  agency: { basic: [], advanced: [] },
  me: { basic: [], advanced: [] },
};

/**
 * The cards this screen shows, in order.
 *
 * An empty choice is "never chose", answered with the default. Unknown ids
 * drop and the list is capped, the same leniency the server applies on
 * read, so a document from another build still renders.
 *
 * @param view - Whose overview
 * @param mode - Basic or advanced
 * @param chosen - The stored choice for this view and mode
 * @returns Known ids, at most the mode's limit
 */
export function resolveTileIds(view: OverviewView, mode: OverviewMode, chosen: string[] | undefined): string[] {
  const known = new Set(TILE_CATALOGUE[view].map((entry) => entry.id));
  const ids = chosen?.length ? chosen : DEFAULT_TILES[view][mode];
  return ids.filter((id) => known.has(id)).slice(0, TILE_LIMITS[mode]);
}

/**
 * Builds the cards for a list of ids, in that order.
 *
 * @param view - Whose overview, which decides the vocabulary
 * @param ids - Card ids, already resolved
 * @param ctx - Payload, agency and translations
 * @returns The cards ready to render
 */
export function buildTiles(view: OverviewView, ids: string[], ctx: TileContext): Tile[] {
  const byId = new Map(TILE_CATALOGUE[view].map((entry) => [entry.id, entry]));
  return ids.flatMap((id) => {
    const entry = byId.get(id);
    return entry ? [entry.build(ctx)] : [];
  });
}

/** One heading on the screen and the cards under it. */
export interface CategoryGroup<T> {
  category: TileCategory;
  items: T[];
}

/**
 * Splits a list into the fixed section order, keeping the list's own order
 * inside each section, and drops the sections left empty.
 *
 * The sections do not move: a person picks and orders cards, and the screen
 * keeps the headings where everyone expects them. A heading over nothing is
 * not drawn.
 *
 * @param view - Whose overview, which says each card's section
 * @param items - Anything carrying a card id, in order
 * @param idOf - How to read the card id from an item
 * @returns The non-empty sections, in the fixed order
 */
export function groupByCategory<T>(
  view: OverviewView, items: T[], idOf: (item: T) => string,
): CategoryGroup<T>[] {
  const categoryOf = new Map(TILE_CATALOGUE[view].map((entry) => [entry.id, entry.category]));
  return TILE_CATEGORIES
    .map((category) => ({
      category,
      items: items.filter((item) => categoryOf.get(idOf(item)) === category),
    }))
    .filter((group) => group.items.length > 0);
}
